import React, { Suspense, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ClientState } from "state/state";
import { isPermitted } from "state/node/selectors";
import {
    commentsFutureSliceLoad,
    commentsPastSliceLoad,
    commentsScrolledToAnchor,
    commentsScrolledToComments,
    commentsScrolledToComposer,
    detailedPostingScrolledToGallery
} from "state/detailedposting/actions";
import {
    getCommentsState,
    getCommentsWithVisibility,
    getDetailedPosting,
    getVisibleComments,
    isCommentComposerFocused,
    isCommentDialogReady,
    isCommentsFocused,
    isCommentsShowInvisible,
    isDetailedPostingCached,
    isDetailedPostingGalleryFocused
} from "state/detailedposting/selectors";
import { isAtDetailedPostingPage } from "state/navigation/selectors";
import { useDispatcher } from "ui/hook";
import { FeedTopBox } from "ui/control";
import CommentsNavigator from "ui/comment/CommentsNavigator";
import CommentComposeLine from "ui/comment/compose/CommentComposeLine";
import CommentsSentinelLine from "ui/comment/CommentsSentinelLine";
import Comment from "ui/comment/Comment";
import { getPageFooterHeight, getPageHeaderHeight, isElementCompletelyVisible } from "util/ui";
import "./Comments.css";

const CommentDialog = React.lazy(() => import("ui/comment/compose/CommentDialog"));

export default function Comments() {
    const visible = useSelector(isAtDetailedPostingPage);
    const galleryFocused = useSelector(isDetailedPostingGalleryFocused);
    const total = useSelector((state: ClientState) =>
        isDetailedPostingCached(state) ? (getDetailedPosting(state)!.totalComments ?? 0) : 0
    );
    const loadingFuture = useSelector((state: ClientState) => getCommentsState(state).loadingFuture);
    const loadingPast = useSelector((state: ClientState) => getCommentsState(state).loadingPast);
    const before = useSelector((state: ClientState) => getCommentsState(state).before);
    const after = useSelector((state: ClientState) => getCommentsState(state).after);
    const totalInPast = useSelector((state: ClientState) => getCommentsState(state).totalInPast);
    const totalInFuture = useSelector((state: ClientState) => getCommentsState(state).totalInFuture);
    const comments = useSelector((state: ClientState) =>
        isCommentsShowInvisible(state) ? getCommentsWithVisibility(state) : getVisibleComments(state)
    );
    const anchor = useSelector((state: ClientState) => getCommentsState(state).anchor);
    const focusedCommentId = useSelector((state: ClientState) => getCommentsState(state).focusedCommentId);
    const focused = useSelector(isCommentsFocused);
    const composerFocused = useSelector(isCommentComposerFocused);
    const showCommentDialog = useSelector((state: ClientState) =>
        state.detailedPosting.commentDialog.show && isCommentDialogReady(state)
    );
    const commentsVisible = useSelector((state: ClientState) =>
        isPermitted("viewComments", getDetailedPosting(state), "public", state)
    );

    const dispatch = useDispatcher();
    const {t} = useTranslation();

    useEffect(() => {
        if (!visible) {
            return;
        }
        if (galleryFocused) {
            scrollToElement(document.getElementById("posting-gallery")!);
            dispatch(detailedPostingScrolledToGallery());
            return;
        }
        if (focused) {
            scrollToElement(document.getElementById("comments")!);
            dispatch(commentsScrolledToComments());
            return;
        }
        if (composerFocused) {
            scrollToElement(document.getElementById("comment-compose")!);
            const body = document.getElementById("body");
            if (body != null) {
                body.focus();
            }
            dispatch(commentsScrolledToComposer());
            return;
        }
        if (
            anchor != null
            && (anchor <= before || (anchor >= Number.MAX_SAFE_INTEGER && before >= Number.MAX_SAFE_INTEGER))
            && (anchor > after || (anchor <= Number.MIN_SAFE_INTEGER && after <= Number.MIN_SAFE_INTEGER))
        ) {
            if (scrollTo(anchor)) {
                dispatch(commentsScrolledToAnchor());
            }
        }
    }, [dispatch, after, anchor, before, composerFocused, focused, galleryFocused, visible]);

    const topmostMoment = getTopmostMoment();
    useEffect(() => {
        if (anchor == null && topmostMoment != null) {
            scrollTo(topmostMoment);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [after]);

    const loadFuture = () => {
        if (loadingFuture || before >= Number.MAX_SAFE_INTEGER) {
            return;
        }
        dispatch(commentsFutureSliceLoad(null));
    }

    const loadPast = () => {
        if (loadingPast || after <= Number.MIN_SAFE_INTEGER) {
            return;
        }
        dispatch(commentsPastSliceLoad(null));
    }

    const [fromIndex, setFromIndex] = React.useState(-1);
    const [toIndex, setToIndex] = React.useState(-1);

    const onScroll = useCallback(() => {
        const top = getPageHeaderHeight();
        const bottom = window.innerHeight - getPageFooterHeight();
        let topIndex = -1;
        let bottomIndex = -1;
        const comments = document.getElementsByClassName("comment");
        for (let i = 0; i < comments.length; i++) {
            const comment = comments.item(i) as HTMLElement;
            if (comment == null) {
                continue;
            }
            const index = commentIndex(comment);
            if (comment.getBoundingClientRect().top <= top) {
                topIndex = index;
            }
            if (comment.getBoundingClientRect().top > bottom) {
                break;
            }
            bottomIndex = index;
        }
        setFromIndex(topIndex);
        setToIndex(bottomIndex);
    }, []);

    useEffect(() => {
        if (visible) {
            window.addEventListener("scroll", onScroll);
        }
        return () => window.removeEventListener("scroll", onScroll);
    }, [onScroll, visible]);

    if (!commentsVisible) {
        return <div id="comments" className="disabled">{t("comments-disabled")}</div>;
    }

    const empty = comments.length === 0 && !loadingFuture && !loadingPast
        && before >= Number.MAX_SAFE_INTEGER && after <= Number.MIN_SAFE_INTEGER;

    return (
        <>
            <FeedTopBox>
                {fromIndex >= 0 && toIndex >= 0 && total > 1 &&
                    <CommentsNavigator from={fromIndex} to={toIndex} total={total}/>
                }
            </FeedTopBox>
            <div id="comments">
                {empty ||
                    <>
                        {comments.length > 0 &&
                            <CommentsSentinelLine
                                loading={loadingPast}
                                title={t("view-earlier-comments")}
                                total={totalInPast}
                                visible={total > 0 && after > Number.MIN_SAFE_INTEGER}
                                onClick={loadPast}
                            />
                        }
                        {comments.map((comment, index) =>
                            <Comment
                                key={comment.moment}
                                comment={comment}
                                previousId={index > 0 ? comments[index - 1].id : null}
                                focused={comment.id === focusedCommentId}
                                index={totalInPast + index}
                            />
                        )}
                        <CommentsSentinelLine
                            loading={loadingFuture}
                            title={
                                comments.length !== 0
                                    ? t("view-later-comments")
                                    : t("view-comments")
                            }
                            total={totalInFuture}
                            visible={total > 0 && before < Number.MAX_SAFE_INTEGER}
                            onClick={loadFuture}
                        />
                    </>
                }
            </div>
            <CommentComposeLine/>
            {showCommentDialog &&
                <Suspense fallback={null}>
                    <CommentDialog/>
                </Suspense>
            }
        </>
    );
}

function commentMoment(comment: HTMLElement): number {
    return comment.dataset.moment != null ? parseInt(comment.dataset.moment) : 0;
}

function commentIndex(comment: HTMLElement): number {
    return comment.dataset.index != null ? parseInt(comment.dataset.index) : 0;
}

function getTopmostMoment(): number | null {
    const top = getPageHeaderHeight();
    const comments = document.getElementsByClassName("comment");
    for (let i = 0; i < comments.length; i++) {
        const comment = comments.item(i) as HTMLElement;
        if (comment == null) {
            continue;
        }
        if (comment.getBoundingClientRect().top >= top) {
            return commentMoment(comment);
        }
    }
    return null;
}

function getCommentAt(moment: number): HTMLElement | null {
    const comments = document.getElementsByClassName("comment");
    for (let i = 0; i < comments.length; i++) {
        const comment = comments.item(i) as HTMLElement;
        if (comment == null) {
            continue;
        }
        if (commentMoment(comment) >= moment) {
            return comment;
        }
    }
    return null;
}

function scrollTo(moment: number): boolean {
    if (moment === Number.MAX_SAFE_INTEGER) {
        scrollToEnd();
        return true;
    } else {
        const comment = getCommentAt(moment);
        if (comment != null && !isElementCompletelyVisible(comment)) {
            scrollToElement(comment);
        }
        return comment != null;
    }
}

function scrollToEnd(): void {
    setTimeout(() => {
        const y = document.getElementById("comments")!.getBoundingClientRect().bottom;
        window.scrollBy(0, y - getPageHeaderHeight());
    });
}

function scrollToElement(element: Element): void {
    setTimeout(() => {
        const y = element.getBoundingClientRect().top;
        window.scrollBy(0, y - getPageHeaderHeight());
    });
}
